import React, { useMemo } from "react";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";

export type MultiStateCheckboxStatus =
  | null
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "REQUEST_EDIT";

const order: MultiStateCheckboxStatus[] = [
  "PENDING",
  "APPROVED",
  "REJECTED",
  "REQUEST_EDIT",
];

const getNextValue = (
  current: MultiStateCheckboxStatus,
  allowedStatuses?: readonly MultiStateCheckboxStatus[],
): MultiStateCheckboxStatus => {
  const cycle = allowedStatuses
    ? order.filter(
        (status) =>
          status === "PENDING" || allowedStatuses.includes(status),
      )
    : order;
  const index = cycle.indexOf(current);
  return cycle[(index + 1) % cycle.length];
};

const getMeta = (value: MultiStateCheckboxStatus) => {
  switch (value) {
    case "APPROVED":
      return {
        label: "Đồng ý",
        icon: <CheckOutlined style={{ color: "#fff", fontSize: 12 }} />,
        bg: "#1677ff",
        borderColor: "#1677ff",
      };

    case "REJECTED":
      return {
        label: "Từ chối",
        icon: <CloseOutlined style={{ color: "#fff", fontSize: 12 }} />,
        bg: "#ff4d4f",
        borderColor: "#ff4d4f",
      };

    case "REQUEST_EDIT":
      return {
        label: "Sửa lại",
        icon: <EditOutlined style={{ color: "#fff", fontSize: 12 }} />,
        bg: "#faad14",
        borderColor: "#faad14",
      };

    case "PENDING":
    default:
      return {
        label: "Chưa chọn",
        icon: null,
        bg: "#fff",
        borderColor: "#d9d9d9",
      };
  }
};

type MultiStateCheckboxProps = {
  value?: MultiStateCheckboxStatus;
  onChange?: (value: MultiStateCheckboxStatus) => void;
  disabled?: boolean;
  allowedStatuses?: readonly MultiStateCheckboxStatus[];
};

export const MultiStateCheckbox: React.FC<MultiStateCheckboxProps> = ({
  value = null,
  onChange,
  disabled = false,
  allowedStatuses,
}) => {
  const meta = useMemo(() => getMeta(value), [value]);

  const handleClick = () => {
    if (disabled) return;
    onChange?.(getNextValue(value, allowedStatuses));
  };

  return (
    <div
      onClick={handleClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          border: `1px solid ${meta.borderColor}`,
          borderRadius: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: meta.bg,
          transition: "all 0.2s ease",
          boxSizing: "border-box",
        }}
      >
        {meta.icon}
      </div>

      <span>{meta.label}</span>
    </div>
  );
};
